import React, { useEffect, useState } from 'react';
import { Box, Tabs, Tab, AppBar, Toolbar, Typography, IconButton, Button, ToggleButton, ToggleButtonGroup, Drawer, CssBaseline } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import HomeIcon from '@mui/icons-material/Home';
import { useNavigate } from 'react-router-dom';
import { useAuth } from "react-oidc-context";
import { performSignOut } from "../signOut";

import { useFormContext } from '../context/FormContext';
import Sidebar_imp from './Sidebar_imp';
import ImpactionTab_Position from './ImpactionTab_Position';
import ImpactionTab_Roots from './ImpactionTab_Roots';
import ImpactionTab_IAC from './ImpactionTab_IAC';
import ImpactionTab_Adjacent from './ImpactionTab_Adjacent';
import ImpactionTab_Measurements from './ImpactionTab_Measurements';
import ImpactionTab_Generate from './ImpactionTab_Generate';

// Maxilla Components
import ImpactionTab_Position_Maxilla from './ImpactionTab_Position_Maxilla';
import ImpactionTab_Adjacent_Maxilla from './ImpactionTab_Adjacent_Maxilla';

import { IMPACTION_TEETH } from './ImpactionConstants';
import ScrollToTopButton from './ScrollToTopButton';
import SaveButton from './SaveButton';

const drawerWidth = 340;

function TabPanel(props) {
    const { children, value, index, ...other } = props;
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`impaction-tabpanel-${index}`}
            aria-labelledby={`impaction-tab-${index}`}
            {...other}
            style={{ width: '100%' }} // Ensure full width
        >
            {value === index && (
                <Box sx={{ p: 0 }}>
                    {children}
                </Box>
            )}
        </div>
    );
}

function ImpactionReport() {
    const { state, actions } = useFormContext();
    const navigate = useNavigate();
    const auth = useAuth();

    // Determine default tooth based on report type
    const isMaxilla = state.reportType === 'Maxilla';
    const defaultTooth = isMaxilla ? '13' : '38';

    const [selectedTooth, setSelectedTooth] = useState(defaultTooth);

    // Reset loop when report type changes
    useEffect(() => {
        const isMax = state.reportType === 'Maxilla';
        setSelectedTooth(isMax ? '13' : '38');
        actions.setCurrentTab(0);
    }, [state.reportType]);

    const handleChange = (event, newValue) => {
        actions.setCurrentTab(newValue);
    };

    const handleToothChange = (event, newTooth) => {
        if (newTooth !== null) {
            setSelectedTooth(newTooth);
        }
    };

    return (
        <Box sx={{ display: 'flex' }}>
            <CssBaseline />
            {/* App Bar */}
            <AppBar
                position="fixed"
                sx={{
                    zIndex: 1201,
                    width: `calc(100% - ${state.isSidebarOpen ? drawerWidth : 0}px)`,
                    ml: state.isSidebarOpen ? `${drawerWidth}px` : 0,
                    transition: (theme) => theme.transitions.create(['margin', 'width'], {
                        easing: theme.transitions.easing.sharp,
                        duration: theme.transitions.duration.leavingScreen,
                    }),
                }}
            >
                <Toolbar>
                    <IconButton
                        color="inherit"
                        aria-label="open drawer"
                        onClick={actions.toggleSidebar}
                        edge="start"
                        sx={{ mr: 2 }}
                    >
                        <MenuIcon />
                    </IconButton>

                    <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
                        {isMaxilla ? 'Impaction Report (Maxilla)' : 'Impaction Report (Mandible)'}
                    </Typography>

                    {/* Tooth Selector - MOVED TO POSITION TAB */}

                    <Button
                        color="inherit"
                        startIcon={<HomeIcon />}
                        onClick={() => navigate('/')}
                        sx={{ mr: 2 }}
                    >
                        Home
                    </Button>
                    {/* Add conditional text if needed, but keeping it simple as requested */}
                    <Button color="inherit" onClick={() => performSignOut()}>Sign out</Button>
                    <SaveButton />
                </Toolbar>
            </AppBar>

            {/* Sidebar Drawer */}
            <Drawer
                sx={{
                    width: drawerWidth,
                    flexShrink: 0,
                    '& .MuiDrawer-paper': {
                        width: drawerWidth,
                        boxSizing: 'border-box',
                    },
                }}
                variant="persistent"
                anchor="left"
                open={state.isSidebarOpen}
            >
                <Sidebar_imp />
            </Drawer>

            {/* Main Content Area */}
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    p: 3,
                    transition: (theme) => theme.transitions.create('margin', {
                        easing: theme.transitions.easing.sharp,
                        duration: theme.transitions.duration.leavingScreen,
                    }),
                    marginLeft: `-${drawerWidth}px`,
                    ...(state.isSidebarOpen && {
                        transition: (theme) => theme.transitions.create('margin', {
                            easing: theme.transitions.easing.easeOut,
                            duration: theme.transitions.duration.enteringScreen,
                        }),
                        marginLeft: 0,
                    }),
                }}
            >
                <Toolbar /> {/* Spacer */}

                {/* Tabs Area */}
                <Box sx={{ borderBottom: 1, borderColor: 'divider', mt: 4 }}>
                    <Tabs
                        value={state.currentTab}
                        onChange={handleChange}
                        variant="scrollable"
                        scrollButtons="auto"
                        aria-label="impaction tabs"
                    >
                        <Tab label="Development" />
                        {!isMaxilla && <Tab label="Measurements" />}
                        <Tab label={isMaxilla ? "Roots & Sinus" : "Roots & Canal"} />
                        <Tab label={isMaxilla ? "Adjacent Teeth" : "IAC Relationship"} />
                        <Tab label={isMaxilla ? "Generate Report" : "Adjacent & Ramus"} />
                        {!isMaxilla && <Tab label="Generate Report" />}
                    </Tabs>
                </Box>

                <Box sx={{ p: 2, pb: 10 }}>
                    <TabPanel value={state.currentTab} index={0}>
                        {isMaxilla ? (
                            <ImpactionTab_Position_Maxilla tooth={selectedTooth} />
                        ) : (
                            <ImpactionTab_Position tooth={selectedTooth} />
                        )}
                    </TabPanel>

                    <TabPanel value={state.currentTab} index={1}>
                        {isMaxilla ? (
                            <ImpactionTab_Roots tooth={selectedTooth} />
                        ) : (
                            <ImpactionTab_Measurements tooth={selectedTooth} />
                        )}
                    </TabPanel>

                    <TabPanel value={state.currentTab} index={2}>
                        {isMaxilla ? (
                            <ImpactionTab_Adjacent_Maxilla tooth={selectedTooth} />
                        ) : (
                            <ImpactionTab_Roots tooth={selectedTooth} />
                        )}
                    </TabPanel>

                    <TabPanel value={state.currentTab} index={3}>
                        {isMaxilla ? (
                            <ImpactionTab_Generate tooth={selectedTooth} />
                        ) : (
                            <ImpactionTab_IAC tooth={selectedTooth} />
                        )}
                    </TabPanel>

                    <TabPanel value={state.currentTab} index={4}>
                        {isMaxilla ? null : <ImpactionTab_Adjacent tooth={selectedTooth} />}
                    </TabPanel>

                    <TabPanel value={state.currentTab} index={5}>
                        {!isMaxilla && <ImpactionTab_Generate tooth={selectedTooth} />}
                    </TabPanel>
                </Box>
            </Box>
            <ScrollToTopButton />
        </Box>
    );
}

export default ImpactionReport;
